import { getContactName, getModel } from '@hooks/API/Contact/UseContactsList';

/**
 * Email utilities library for SendProjectDrawer
 * Contains all functions related to building email items and managing document selection
 */

/**
 * Creates an empty email items structure
 */
export const getEmptyEmailItems = () => ({
    ProjectDocuments: [],
    Recipients: [],
});

/**
 * Creates an empty recipient structure
 */
export const getEmptyRecipient = () => ({
    ModelData: {
        Name: '',
        CG: null,
        ST: null,
    },
    Selected: true,
    Emails: [],
    DocVersion: {},
    Attachments: [],
});

/**
 * Checks if a document is associated with a model
 */
export const isDocumentOnModel = (doc) => {
    if (doc.ContactID) {
        return true;
    }
    return false;
};

/**
 * Checks if a document is associated with an event
 */
export const isDocumentOnEvent = (doc) => {
    if (doc.EventID || doc.FollowUpID || doc.TravelID || doc.AccommodationID) {
        return true;
    }
    return false;
};

/**
 * Gets general project documents (not associated with specific models or events)
 */
export const getGeneralProjectDocuments = (currentProject) => {
    const docs = [];
    if (currentProject.Documents && currentProject.Documents.length > 0) {
        const generaldocs = currentProject.Documents.filter(
            (doc) => !isDocumentOnModel(doc) && !isDocumentOnEvent(doc)
        );
        docs.push(...generaldocs);
    }
    return docs;
};

/**
 * Checks if an event ID is in the event dates models
 */
export const isEventIDInEventDatesModels = (eventID, eventDatesModels, currentProject) => {
    const selectedEvent = currentProject.Events.find((ev) => ev.ID === eventID);
    if (selectedEvent && selectedEvent.EventDates) {
        for (const evdate of selectedEvent.EventDates) {
            if (evdate.EventDatesModels && evdate.EventDatesModels.length > 0) {
                for (const evdatemodel of evdate.EventDatesModels) {
                    if (eventDatesModels.includes(evdatemodel.ID)) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
};

/**
 * Checks if a document is on selected events
 */
export const isDocumentOnSelectedEvents = (doc, docversion, currentProject) => {
    if (
        doc.EventID &&
        docversion.EventDatesModels &&
        docversion.EventDatesModels.length > 0 &&
        isEventIDInEventDatesModels(doc.EventID, docversion.EventDatesModels, currentProject)
    ) {
        return true;
    }
    if (
        doc.TravelID &&
        docversion.Travels &&
        docversion.Travels.length > 0 &&
        docversion.Travels.some((tr) => tr === doc.TravelID)
    ) {
        return true;
    }
    if (
        doc.FollowUpID &&
        docversion.FollowUps &&
        docversion.FollowUps.length > 0 &&
        docversion.FollowUps.some((fu) => fu === doc.FollowUpID)
    ) {
        return true;
    }
    if (
        doc.AccommodationID &&
        docversion.Accommodations &&
        docversion.Accommodations.length > 0 &&
        docversion.Accommodations.some((ac) => ac === doc.AccommodationID)
    ) {
        return true;
    }
    return false;
};

/**
 * Checks if a document is for a specific model
 */
export const isDocumentForModel = (doc, docversion, currentProject) => {
    if (isDocumentOnModel(doc)) {
        if (doc.ContactID === docversion.ModelID) {
            if (isDocumentOnEvent(doc)) {
                return isDocumentOnSelectedEvents(doc, docversion, currentProject);
            } else {
                return true;
            }
        }
    } else {
        if (isDocumentOnEvent(doc)) {
            return isDocumentOnSelectedEvents(doc, docversion, currentProject);
        }
    }
    return false;
};

/**
 * Builds email items from document version data
 */
export const buildEmailItems = (docversiondata, currentProject) => {
    const newEmailItems = getEmptyEmailItems();
    const projectDocuments = getGeneralProjectDocuments(currentProject);

    if (projectDocuments && projectDocuments.length > 0) {
        newEmailItems.ProjectDocuments.push(
            ...projectDocuments.map((projectdocument) => ({
                DocumentID: projectdocument.ID,
                DocumentData: projectdocument,
                Selected: false,
            }))
        );
    }

    for (const docversion of docversiondata) {
        const model = getModel(docversion.ModelID);
        const newRecipient = getEmptyRecipient();
        newRecipient.ModelData = {
            ID: docversion.ModelID,
            Name: model.N + ' ' + model.S,
            CG: model.CG,
            ST: model.ST,
        };
        newRecipient.DocVersion = docversion;
        newRecipient.Emails = model.E ? [{ email: model.E, ccs: model.ECC ? model.ECC : [] }] : [];

        const modelDocuments = currentProject.Documents.filter((doc) =>
            isDocumentForModel(doc, docversion, currentProject)
        );

        if (modelDocuments && modelDocuments.length > 0) {
            newRecipient.Attachments.push(
                ...modelDocuments.map((modeldocument) => ({
                    DocumentID: modeldocument.ID,
                    DocumentData: modeldocument,
                    Selected: true,
                }))
            );
        }
        newEmailItems.Recipients.push(newRecipient);
    }

    return newEmailItems;
};

/**
 * Updates project document selection
 */
export const updateProjectDocumentSelection = (emailItems, documentID, selected) => {
    const newEmailItems = { ...emailItems };
    const generalDocument = newEmailItems.ProjectDocuments.find((doc) => doc.DocumentID === documentID);
    if (generalDocument) {
        generalDocument.Selected = selected;
    }
    return newEmailItems;
};

/**
 * Updates model document selection
 */
export const updateModelDocumentSelection = (emailItems, modelID, documentID, selected) => {
    const newEmailItems = { ...emailItems };
    const recipient = newEmailItems.Recipients.find((rec) => rec.ModelData.ID === modelID);
    if (recipient) {
        const modelDocument = recipient.Attachments.find((doc) => doc.DocumentID === documentID);
        if (modelDocument) {
            modelDocument.Selected = selected;
        }
    }
    return newEmailItems;
};

/**
 * Updates model selection for email
 */
export const updateModelSelection = (emailItems, modelID, selected) => {
    const newEmailItemsRecipients = emailItems.Recipients.map((recipient) => {
        if (recipient.ModelData.ID === modelID) {
            return {
                ...recipient,
                Selected: selected,
            };
        } else {
            return recipient;
        }
    });
    return { ...emailItems, Recipients: [...newEmailItemsRecipients] };
};

/**
 * Adds an email to a model
 */
export const addEmailToModel = (emailItems, modelID, email) => {
    const newEmailItemsRecipients = emailItems.Recipients.map((recipient) => {
        if (recipient.ModelData.ID === modelID) {
            const newModelEmails = [...recipient.Emails, email];
            return {
                ...recipient,
                Emails: newModelEmails,
                Selected: true,
            };
        } else {
            return recipient;
        }
    });
    return { ...emailItems, Recipients: [...newEmailItemsRecipients] };
};

/**
 * Removes an email from a model
 */
export const removeEmailFromModel = (emailItems, modelID, email) => {
    const newEmailItemsRecipients = emailItems.Recipients.map((recipient) => {
        if (recipient.ModelData.ID === modelID) {
            const newModelEmails = [...recipient.Emails.filter((e) => e.email !== email)];
            return {
                ...recipient,
                Emails: newModelEmails,
                Selected: newModelEmails.length === 0 ? false : recipient.Selected,
            };
        } else {
            return recipient;
        }
    });
    return { ...emailItems, Recipients: [...newEmailItemsRecipients] };
};
